/**
 * Inbox notifications. Class only — instance lives in integrations-registry.js.
 * Persist/rehydrate and tenant refetch import the registry (not client.js) to avoid cycles.
 * No toast on GET or mark-read (401 silent; other errors console only).
 */
import { getHttpErrorStatus } from '@/lib/get-http-error-status.js';
import {
    NOTIFICATION_LIST_PAGE_SIZE,
    NOTIFICATION_PAGE_SIZE,
    buildNotificationInboxQuery,
    inboxEventActionParams,
} from '@/lib/notifications/build-notification-inbox-query.js';
import { mapNotificationRows } from '@/lib/notifications/map-notification-row.js';
import { useNotificationStore } from '@/store/notification.store.js';
import { Logger } from '@/lib/app-log.js';

export class NotificationIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async getMyInbox(params = {}) {
        return this.service.getMyAll(params);
    }

    async fetchNotifications(page = 1, append = false) {
        useNotificationStore.setState(append ? { isLoadingMore: true } : { isLoading: true });
        try {
            const response = await this.service.getMyAll(
                buildNotificationInboxQuery({
                    page,
                    limit: NOTIFICATION_PAGE_SIZE,
                    includeUniqueValues: false,
                }),
            );
            if (response.success) {
                const total = Number(response.total) || 0;
                const limit = Number(response.limit) || NOTIFICATION_PAGE_SIZE;
                const mapped = mapNotificationRows(response.data);
                useNotificationStore.setState((state) => {
                    let notifications = mapped;
                    if (append) {
                        const existingIds = new Set(state.notifications.map((n) => n.id));
                        const unique = mapped.filter((n) => !existingIds.has(n.id));
                        notifications = [...state.notifications, ...unique];
                    }
                    return {
                        notifications,
                        unreadCount: response.unreadCount ?? state.unreadCount,
                        total,
                        page,
                        hasMore: total > page * limit,
                        isLoading: false,
                        isLoadingMore: false,
                    };
                });
            } else {
                useNotificationStore.setState({ isLoading: false, isLoadingMore: false });
            }
        } catch (error) {
            const status = getHttpErrorStatus(error);
            if (status !== 401) {
                Logger.error('Fetch notifications failed', { error: error?.message });
            }
            useNotificationStore.setState({ isLoading: false, isLoadingMore: false });
        }
    }

    async fetchNotificationList(params = {}, options = {}) {
        const { silent = false } = options;
        const state = useNotificationStore.getState();
        const effectiveParams = { ...state.listSearchParams, ...params };
        if (!silent) useNotificationStore.setState({ listLoading: true });
        try {
            const actionQuery = inboxEventActionParams(effectiveParams.eventGroup);
            const response = await this.service.getMyAll(
                buildNotificationInboxQuery({
                    page: effectiveParams.page ?? 1,
                    limit: effectiveParams.pageSize ?? NOTIFICATION_LIST_PAGE_SIZE,
                    search: effectiveParams.search || undefined,
                    sortBy: effectiveParams.sortBy || 'createdAt',
                    sortOrder: effectiveParams.sortOrder || 'desc',
                    type: effectiveParams.type || undefined,
                    read: effectiveParams.read || undefined,
                    eventActions: actionQuery.eventActions,
                    excludeEventActions: actionQuery.excludeEventActions,
                    includeUniqueValues: true,
                }),
            );

            if (response.success) {
                const meta = response.meta || {};
                const pageSize = meta.pageSize || effectiveParams.pageSize || NOTIFICATION_LIST_PAGE_SIZE;
                const page = meta.page || effectiveParams.page || 1;
                const total = meta.total ?? response.total ?? 0;
                useNotificationStore.setState({
                    listItems: mapNotificationRows(response.data),
                    listSearchParams: effectiveParams,
                    listPaginationData: {
                        page,
                        pageSize,
                        total,
                        totalPages: meta.totalPages ?? (Math.ceil(total / pageSize) || 0),
                    },
                    listUniqueValues: response.uniqueValues || state.listUniqueValues,
                    unreadCount: response.unreadCount ?? state.unreadCount,
                    listLoading: false,
                    listInitialized: true,
                });
            } else {
                useNotificationStore.setState({ listLoading: false });
            }
        } catch (error) {
            const status = getHttpErrorStatus(error);
            if (status !== 401) {
                Logger.error('Fetch notification list failed', { error: error?.message });
            }
            useNotificationStore.setState({ listLoading: false });
        }
    }

    async loadMoreNotifications() {
        const { hasMore, isLoading, isLoadingMore, page } = useNotificationStore.getState();
        if (!hasMore || isLoading || isLoadingMore) return;
        await this.fetchNotifications(page + 1, true);
    }

    async markAsRead(id) {
        useNotificationStore.getState().applyMarkAsRead(id);
        try {
            await this.service.markAsRead(id);
        } catch (error) {
            Logger.error('Failed to mark as read API', { error: error?.message });
        }
    }

    async markAllAsRead() {
        useNotificationStore.getState().applyMarkAllAsRead();
        try {
            await this.service.markAllAsRead();
        } catch (error) {
            Logger.error('Failed to mark all as read API', { error: error?.message });
        }
    }

    async registerPushToken(token) {
        if (!token) return;
        try {
            const result = await this.service.registerPushToken(token);
            Logger.debug('FCM Token registered successfully');
            return result;
        } catch (error) {
            Logger.error('Failed to register notification token', { error: error?.message });
        }
    }
}
